import hash from 'object-hash';

import {
  ReportDashboardComponentKind,
  ReportDashboardComponentSettings,
  ReportDashboardComponentViewDataNonTimeBased,
  ReportDashboardComponentViewDataTimeBased,
  ReportDashboardMetricOptions,
  ReportResultsDataParams,
} from '@core/reports-types';

import { encodeFiltersQuery } from '../index';

type DynamicKeysBase =
  | 'metric'
  | 'options'
  | 'primaryGroup'
  | 'secondaryGroup'
  | 'sortBy'
  | 'sortOrder'
  | 'filters'
  | 'dateFilter'
  | 'dateField'
  | 'kind';

// TODO shared-analytics - there are now 2 types, one in recruitee and one here
// I feel like maybe this type is too weird and too specific. Let's see how this turns out and remove one of them then
type ComponentDataProps = {
  settings?: Pick<ReportDashboardComponentSettings, 'interval'>;
} & (
  | Pick<ReportDashboardComponentViewDataNonTimeBased, DynamicKeysBase>
  | Pick<ReportDashboardComponentViewDataTimeBased, DynamicKeysBase | 'startPoint' | 'endPoint'>
);

// TODO shared-analytics - this should be part of internal API of results facade
export const buildApiDataParamsHash = (params: ReportResultsDataParams) => hash.sha1({ params }); // On the backend these params are always set on the component if we do not provide it.
// We actually do not provided as in the component builder only properties that make sense are set.
// This fallback ensures that saved component and component in edit mode have the same params hash
// Now builder component will update alongside the saved component when dashboard refreshes.
// See task #863gt1f1c
const normalizeComponentOpts = (component: ComponentDataProps): ComponentDataProps => ({
  ...component,
  options: {
    ...component.options,
    includeArchivedJobs: component.options.includeArchivedJobs ?? true,
    includeArchivedRequisitions: component.options.includeArchivedRequisitions ?? true,
    includeDeletedCandidates: component.options.includeDeletedCandidates ?? false,
    showAllData: component.options.showAllData ?? false,
  },
});
const getComponentApiDataParams = (
  component: ComponentDataProps,
  metric: ReportDashboardMetricOptions,
): ReportResultsDataParams => ({
  metric: component.metric,
  includeDeletedCandidates: component.options.includeDeletedCandidates,
  includeArchivedJobs: component.options.includeArchivedJobs,
  includeArchivedRequisitions: component.options.includeArchivedRequisitions,
  showAllData: component.options.showAllData,
  primaryGroup: component.primaryGroup,
  secondaryGroup: component.secondaryGroup,
  sortBy: component.sortBy,
  sortOrder: component.sortOrder,
  filters: encodeFiltersQuery(component.filters || []),
  interval: component.settings?.interval,
  dateField: component.dateField.field,
  dateResource: component.dateField.resource,
  unitPrecision: metric.unitPrecision,
  ...(component.options.showAllData
    ? {
        dateRange: null,
        dateStart: null,
        dateEnd: null,
      }
    : component.dateFilter),
  ...(component.kind === ReportDashboardComponentKind.TimeBased && {
    startPoint: component.startPoint,
    endPoint: component.endPoint,
  }),
});
export const buildApiDataParams = (
  component: ComponentDataProps,
  metric: ReportDashboardMetricOptions,
): ReportResultsDataParams => {
  const componentData = normalizeComponentOpts(component);
  return getComponentApiDataParams(componentData, metric);
};
